import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import AdminShell from "../../components/AdminShell";
import { api, formatApiError, formatCDF } from "../../lib/api";
import { Badge, Button, Card, Input, Label } from "../../components/ui-kit";

const emptyCity = { name: "", country: "RDC", lat: "", lng: "", service_radius_km: 30, featured: true, active: true };
const emptyPromo = { code: "", description: "", type: "percent", value: 10, max_discount: 5000, min_amount: 0, active: true };

export default function AdminLaunch() {
  const [launch, setLaunch] = useState(null);
  const [cities, setCities] = useState([]);
  const [promos, setPromos] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [cityForm, setCityForm] = useState(emptyCity);
  const [promoForm, setPromoForm] = useState(emptyPromo);
  const [payment, setPayment] = useState({});

  const load = () => {
    Promise.all([
      api.get("/admin/launch/status"),
      api.get("/admin/cities"),
      api.get("/admin/promos"),
      api.get("/admin/ad-campaigns"),
    ])
      .then(([l, c, p, a]) => {
        setLaunch(l.data);
        setCities(Array.isArray(c.data) ? c.data : []);
        setPromos(Array.isArray(p.data) ? p.data : []);
        setCampaigns(Array.isArray(a.data) ? a.data : []);
      })
      .catch((e) => toast.error(formatApiError(e)));
  };

  useEffect(() => { load(); }, []);

  const saveCity = async () => {
    try {
      const payload = {
        ...cityForm,
        lat: Number(cityForm.lat),
        lng: Number(cityForm.lng),
        service_radius_km: Number(cityForm.service_radius_km),
      };
      if (cityForm.id) await api.put(`/admin/cities/${cityForm.id}`, payload);
      else await api.post("/admin/cities", payload);
      toast.success("Ville enregistree");
      setCityForm(emptyCity);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const savePromo = async () => {
    try {
      const payload = {
        ...promoForm,
        code: promoForm.code.toUpperCase(),
        value: Number(promoForm.value),
        max_discount: Number(promoForm.max_discount),
        min_amount: Number(promoForm.min_amount),
      };
      if (promos.some((p) => p.code === payload.code)) await api.put(`/admin/promos/${payload.code}`, payload);
      else await api.post("/admin/promos", payload);
      toast.success("Promo enregistree");
      setPromoForm(emptyPromo);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveAdPayment = async (campaignId) => {
    try {
      const payload = {
        sponsor_name: payment[campaignId]?.sponsor_name || "Annonceur",
        sponsor_phone: payment[campaignId]?.sponsor_phone || "",
        amount: Number(payment[campaignId]?.amount || 0),
        method: payment[campaignId]?.method || "mobile_money",
        reference: payment[campaignId]?.reference || "",
        status: "PAID",
      };
      await api.post(`/admin/ad-campaigns/${campaignId}/payment`, payload);
      toast.success("Paiement annonceur enregistre");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <AdminShell title="Pre-lancement">
      <div className="admin-page-header">
        <div>
          <h1 className="responsive-title font-heading text-3xl font-black tracking-tighter sm:text-4xl">Pre-lancement officiel</h1>
          <p className="text-sm text-muted-foreground">Villes, zones, promos, publicites, sauvegarde et monitoring.</p>
        </div>
      </div>

      <div className="grid gap-5 xl:grid-cols-[1fr_420px]">
        <div className="space-y-5">
          <Card className="p-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="font-heading text-xl font-bold">Checklist production</h2>
                <p className="text-xs text-muted-foreground">{launch?.ready_count || 0}/{launch?.total_count || 0} points prets</p>
              </div>
              <Badge tone={(launch?.ready_count || 0) === launch?.total_count ? "success" : "warning"}>Controle</Badge>
            </div>
            <div className="mt-4 grid gap-2 md:grid-cols-2">
              {(launch?.checks || []).map((check) => (
                <div key={check.key} className="rounded-2xl bg-white/5 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-bold">{check.label}</div>
                    <Badge tone={check.ok ? "success" : "danger"}>{check.ok ? "OK" : "A faire"}</Badge>
                  </div>
                  <div className="mt-1 text-[11px] text-muted-foreground">{check.note}</div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-5">
            <h2 className="font-heading text-xl font-bold">Villes et zones</h2>
            <div className="mt-3 grid gap-3 md:grid-cols-3">
              <div><Label>Ville</Label><Input value={cityForm.name} onChange={(e) => setCityForm((f) => ({ ...f, name: e.target.value }))} /></div>
              <div><Label>Latitude</Label><Input type="number" value={cityForm.lat} onChange={(e) => setCityForm((f) => ({ ...f, lat: e.target.value }))} /></div>
              <div><Label>Longitude</Label><Input type="number" value={cityForm.lng} onChange={(e) => setCityForm((f) => ({ ...f, lng: e.target.value }))} /></div>
              <div><Label>Rayon km</Label><Input type="number" value={cityForm.service_radius_km} onChange={(e) => setCityForm((f) => ({ ...f, service_radius_km: e.target.value }))} /></div>
              <label className="flex items-center justify-between rounded-2xl bg-white/5 p-3 text-sm font-bold">Active <input type="checkbox" checked={cityForm.active} onChange={(e) => setCityForm((f) => ({ ...f, active: e.target.checked }))} /></label>
              <label className="flex items-center justify-between rounded-2xl bg-white/5 p-3 text-sm font-bold">Featured <input type="checkbox" checked={cityForm.featured} onChange={(e) => setCityForm((f) => ({ ...f, featured: e.target.checked }))} /></label>
            </div>
            <Button onClick={saveCity} className="mt-3 w-full">{cityForm.id ? "Modifier la ville" : "Ajouter la ville"}</Button>
            <div className="mt-4 grid gap-2 md:grid-cols-2">
              {cities.map((city) => (
                <button key={city.id} onClick={() => setCityForm(city)} className="rounded-2xl bg-white/5 p-3 text-left hover:bg-white/10">
                  <div className="flex items-center justify-between"><span className="font-bold">{city.name}</span><Badge tone={city.active === false ? "danger" : "success"}>{city.active === false ? "Off" : "On"}</Badge></div>
                  <div className="text-xs text-muted-foreground">{city.lat}, {city.lng} - {city.service_radius_km || 30} km</div>
                </button>
              ))}
            </div>
          </Card>
        </div>

        <div className="space-y-5">
          <Card className="p-5">
            <h2 className="font-heading text-xl font-bold">Promos sans code</h2>
            <div className="mt-3 space-y-3">
              <div><Label>Code</Label><Input value={promoForm.code} onChange={(e) => setPromoForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} /></div>
              <div><Label>Description</Label><Input value={promoForm.description} onChange={(e) => setPromoForm((f) => ({ ...f, description: e.target.value }))} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Type</Label><select value={promoForm.type} onChange={(e) => setPromoForm((f) => ({ ...f, type: e.target.value }))} className="w-full rounded-xl border border-white/10 bg-background px-4 py-3"><option value="percent">%</option><option value="fixed">Fixe</option></select></div>
                <div><Label>Valeur</Label><Input type="number" value={promoForm.value} onChange={(e) => setPromoForm((f) => ({ ...f, value: e.target.value }))} /></div>
                <div><Label>Max discount</Label><Input type="number" value={promoForm.max_discount} onChange={(e) => setPromoForm((f) => ({ ...f, max_discount: e.target.value }))} /></div>
                <div><Label>Min course</Label><Input type="number" value={promoForm.min_amount} onChange={(e) => setPromoForm((f) => ({ ...f, min_amount: e.target.value }))} /></div>
              </div>
              <label className="flex items-center justify-between rounded-2xl bg-white/5 p-3 text-sm font-bold">Active <input type="checkbox" checked={promoForm.active} onChange={(e) => setPromoForm((f) => ({ ...f, active: e.target.checked }))} /></label>
              <Button onClick={savePromo} className="w-full">Enregistrer promo</Button>
            </div>
            <div className="mt-4 space-y-2">
              {promos.map((promo) => (
                <button key={promo.code} onClick={() => setPromoForm(promo)} className="w-full rounded-2xl bg-white/5 p-3 text-left hover:bg-white/10">
                  <div className="flex items-center justify-between"><span className="font-mono font-bold">{promo.code}</span><Badge tone={promo.active ? "success" : "danger"}>{promo.active ? "Active" : "Off"}</Badge></div>
                  <div className="text-xs text-muted-foreground">{promo.description} - {promo.value}{promo.type === "percent" ? "%" : " CDF"}</div>
                </button>
              ))}
            </div>
          </Card>

          <Card className="p-5">
            <h2 className="font-heading text-xl font-bold">Paiements publicites</h2>
            <div className="mt-3 space-y-3">
              {campaigns.slice(0, 8).map((c) => (
                <div key={c.id} className="rounded-2xl bg-white/5 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-bold">{c.title}</div>
                      <div className="text-xs text-muted-foreground">{c.sponsor_payment ? `${c.sponsor_payment.sponsor_name} - ${formatCDF(c.sponsor_payment.amount)}` : "Aucun paiement annonceur"}</div>
                    </div>
                    <Badge tone={c.sponsor_paid ? "success" : "warning"}>{c.sponsor_paid ? "Paye" : "A encaisser"}</Badge>
                  </div>
                  {!c.sponsor_paid && (
                    <div className="mt-3 grid gap-2">
                      <Input placeholder="Nom annonceur" value={payment[c.id]?.sponsor_name || ""} onChange={(e) => setPayment((p) => ({ ...p, [c.id]: { ...p[c.id], sponsor_name: e.target.value } }))} />
                      <Input type="number" placeholder="Montant CDF" value={payment[c.id]?.amount || ""} onChange={(e) => setPayment((p) => ({ ...p, [c.id]: { ...p[c.id], amount: e.target.value } }))} />
                      <Button variant="secondary" onClick={() => saveAdPayment(c.id)}>Marquer paye</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </AdminShell>
  );
}
